import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { LocationOn, WhatsApp } from "@mui/icons-material";
import { sendContactMessage } from "../../api/service";

const ContactUs = () => {
  const [formValues, setFormValues] = useState({
    name: "",
    from: "",
    subject: "",
    contact: "",
    message: "",
    address: "",
    city: "",
    country: "",
    zipCode: "",
  });
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    setAgreeToPolicy(e.target.checked);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!agreeToPolicy) {
      setSnackbar({
        open: true,
        message: "You must agree to the Privacy Policy to proceed.",
        severity: "warning",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await sendContactMessage(formValues);

      // Clear the form on success
      setFormValues({
        name: "",
        from: "",
        subject: "",
        contact: "",
        message: "",
        address: "",
        city: "",
        country: "",
        zipCode: "",
      });
      setAgreeToPolicy(false);

      // Show success message
      setSnackbar({
        open: true,
        message: "Your message has been sent successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error submitting form", error);
      // Show error message
      setSnackbar({
        open: true,
        message: "Failed to send message. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{}}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: "600px", mx: "auto" }}
          >
            Get all our info and also can message us directly from here
          </Typography>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            bgcolor: "white",
            boxShadow: "0 0 20px rgba(0,0,0,0.05)",
          }}
        >
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 600 }}>
                Send us a Message
              </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  "& .MuiTextField-root": {
                    mb: 3,
                  },
                }}
              >
                <TextField
                  fullWidth
                  name="name"
                  label="Name"
                  placeholder="Your full name"
                  required
                  variant="outlined"
                  value={formValues.name}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  name="from"
                  label="Email"
                  placeholder="demo@domain.com"
                  required
                  type="email"
                  variant="outlined"
                  value={formValues.from}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="subject"
                  label="Subject"
                  placeholder="Email Subject"
                  required
                  variant="outlined"
                  value={formValues.subject}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="contact"
                  label="Contact Number"
                  placeholder="01700000000"
                  required
                  variant="outlined"
                  value={formValues.contact}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="address"
                  label="Address"
                  placeholder="Your address"
                  required
                  variant="outlined"
                  value={formValues.address}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="city"
                  label="City"
                  placeholder="Your city"
                  // required
                  variant="outlined"
                  value={formValues.city}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="country"
                  label="Country"
                  placeholder="Your country"
                  // required
                  variant="outlined"
                  value={formValues.country}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="zipCode"
                  label="Zip Code"
                  placeholder="Your zip code"
                  // required
                  variant="outlined"
                  value={formValues.zipCode}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="message"
                  label="Your message"
                  multiline
                  rows={4}
                  required
                  variant="outlined"
                  value={formValues.message}
                  onChange={handleChange}
                  sx={{ mb: 4 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToPolicy}
                      onChange={handleCheckboxChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{" "}
                      <Link
                        href={"/assets/docs/privacy_policy.docx"}
                        target="_blank"
                        sx={{ color: "#215d66", textDecoration: "none" }}
                      >
                        Privacy Policy
                      </Link>
                    </Typography>
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: "#215d66",
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress size={24} sx={{ color: "white", mr: 1 }} />
                      Sending...
                    </Box>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </Box>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 600 }}>
                Get in Touch
              </Typography>
              <Box sx={{ pl: { md: 4 } }}>
                {[
                  {
                    icon: LocationOn,
                    title: "Our Address",
                    content: ["Mohammedpur, Dhaka, Bangladesh"],
                  },
                  {
                    icon: WhatsApp,
                    title: "Whatsapp",
                    content: [
                      "Call or message us on",
                      <Link
                        href="https://wa.me/+8801886212601"
                        target="_blank"
                        sx={{
                          color: "#215d66",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        +880 1886 212601
                      </Link>,
                    ],
                  },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 4,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.08)",
                        transform: "translateX(8px)",
                      },
                    }}
                  >
                    <item.icon
                      sx={{
                        color: "#215d66",
                        mr: 2,
                        fontSize: 28,
                        mt: 0.5,
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {item.title}
                      </Typography>
                      {item.content.map((line, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Success/Error Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactUs;
